// File: frontend/src/pages/KnowledgeGraph/components/ConstellationGraph.tsx
import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Box, Chip, CircularProgress, Tooltip, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';
import HubIcon from '@mui/icons-material/Hub';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { KGClusterResponse, KGNode, KGEdge, MAX_GRAPH_NODES, ENTITY_TYPE_COLORS, ENTITY_TYPE_LABELS } from '../types';
import { buildCytoscapeStyles } from '../styles/nodeStyles';
import { escapeHtml } from '../utils';
import GraphControls, { LegendItem, GraphLayoutMode } from './GraphControls';

// Lazy-load cytoscape to avoid SSR issues
let cytoscape: any = null;
let coseBilkent: any = null;

async function loadCytoscape() {
  if (!cytoscape) {
    const [cyModule, coseModule] = await Promise.all([
      import('cytoscape'),
      import('cytoscape-cose-bilkent'),
    ]);
    cytoscape = cyModule.default;
    coseBilkent = coseModule.default;
    cytoscape.use(coseBilkent);
  }
  return cytoscape;
}

// Smart labels: how many top nodes (by report count) keep a visible label
const TOP_LABEL_COUNT = 12;
// Above this zoom level all labels become visible
const ZOOM_LABEL_THRESHOLD = 1.15;

interface ConstellationGraphProps {
  cluster: KGClusterResponse | null;
  loading: boolean;
  selectedNodeId: string | null;
  rootNodeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
  onExpandNode: (nodeId: string) => void;
  depth: number;
  onDepthChange: (d: number) => void;
  showInferred: boolean;
  onShowInferredChange: (v: boolean) => void;
  truncated: boolean;
  totalNodeCount: number;
  hiddenTypes: string[];
  onToggleType: (type: string) => void;
  legendItems: LegendItem[];
}

const ConstellationGraph: React.FC<ConstellationGraphProps> = ({
  cluster,
  loading,
  selectedNodeId,
  rootNodeId,
  onSelectNode,
  onExpandNode,
  depth,
  onDepthChange,
  showInferred,
  onShowInferredChange,
  truncated,
  totalNodeCount,
  hiddenTypes,
  onToggleType,
  legendItems,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<any>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [, setCyReady] = useState(false);
  const [layoutMode, setLayoutMode] = useState<GraphLayoutMode>('force');

  // Refs so cytoscape event handlers and layout runs always see latest values
  const onSelectNodeRef = useRef(onSelectNode);
  onSelectNodeRef.current = onSelectNode;
  const onExpandNodeRef = useRef(onExpandNode);
  onExpandNodeRef.current = onExpandNode;
  const selectedNodeIdRef = useRef<string | null>(selectedNodeId);
  const rootNodeIdRef = useRef<string | null>(rootNodeId);
  rootNodeIdRef.current = rootNodeId;
  const hiddenTypesRef = useRef<string[]>(hiddenTypes);
  const layoutModeRef = useRef<GraphLayoutMode>(layoutMode);
  const zoomAllLabelsRef = useRef(false);
  const lastRootRef = useRef<string | null>(null);

  const hideTooltip = useCallback(() => {
    if (tooltipRef.current) tooltipRef.current.style.display = 'none';
  }, []);

  const showTooltip = useCallback((html: string, x: number, y: number) => {
    const el = tooltipRef.current;
    if (!el) return;
    el.innerHTML = html;
    el.style.display = 'block';
    el.style.left = `${x + 12}px`;
    el.style.top = `${y - 12}px`;
  }, []);

  // Smart labels: selection + neighborhood + top-N by report count (or all when zoomed in)
  const refreshLabels = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.batch(() => {
      if (zoomAllLabelsRef.current) {
        cy.nodes().addClass('show-label');
        return;
      }
      cy.nodes().removeClass('show-label');
      const visible = cy.nodes().filter((n: any) => !n.hasClass('hidden-type'));
      visible
        .sort((a: any, b: any) => (b.data('report_count') || 0) - (a.data('report_count') || 0))
        .slice(0, TOP_LABEL_COUNT)
        .addClass('show-label');
      const selId = selectedNodeIdRef.current;
      if (selId) {
        const sel = cy.getElementById(selId);
        if (sel.length) sel.closedNeighborhood().nodes().addClass('show-label');
      }
    });
  }, []);

  // Neighborhood focus: dim everything outside the closed neighborhood of a node
  const applyFocus = useCallback((nodeId: string) => {
    const cy = cyRef.current;
    if (!cy) return;
    const node = cy.getElementById(nodeId);
    if (!node.length) return;
    const hood = node.closedNeighborhood();
    cy.batch(() => {
      cy.elements().addClass('faded');
      hood.removeClass('faded');
    });
  }, []);

  // Restore focus to the persistent selection (if any), otherwise clear it.
  // The cluster root is not focused persistently: the initial view stays full.
  const restoreFocus = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const selId = selectedNodeIdRef.current;
    if (selId && selId !== rootNodeIdRef.current && cy.getElementById(selId).length) {
      applyFocus(selId);
    } else {
      cy.elements().removeClass('faded');
    }
  }, [applyFocus]);

  // Layout runner: radial (concentric rings around the root) or force (cose-bilkent)
  const runLayout = useCallback((randomize: boolean) => {
    const cy = cyRef.current;
    if (!cy) return;
    const rootId = rootNodeIdRef.current;

    if (layoutModeRef.current === 'radial' && rootId && cy.getElementById(rootId).length) {
      const root = cy.getElementById(rootId);
      const depths: Record<string, number> = {};
      cy.elements().bfs({
        roots: root,
        visit: (v: any, _e: any, _u: any, _i: number, d: number) => {
          depths[v.id()] = d;
        },
      });
      const maxD = Object.values(depths).reduce((m, d) => Math.max(m, d), 0);
      cy.layout({
        name: 'concentric',
        concentric: (n: any) => maxD + 1 - (depths[n.id()] ?? maxD + 1),
        levelWidth: () => 1,
        minNodeSpacing: 30,
        avoidOverlap: true,
        animate: 'end',
        animationDuration: 400,
        fit: true,
        padding: 40,
      }).run();
      return;
    }

    try {
      cy.layout({
        name: 'cose-bilkent',
        animate: 'end',
        animationDuration: 500,
        randomize,
        nodeRepulsion: 8000,
        idealEdgeLength: 120,
        edgeElasticity: 0.45,
        nestingFactor: 0.1,
        gravity: 0.25,
        numIter: 2500,
        tile: true,
        fit: true,
        padding: 40,
      }).run();
    } catch {
      // Fallback to cose if cose-bilkent fails
      cy.layout({
        name: 'cose',
        animate: true,
        animationDuration: 500,
        fit: true,
        padding: 40,
      }).run();
    }
  }, []);

  // Initialize cytoscape
  useEffect(() => {
    if (isMobile || !containerRef.current) return;

    let mounted = true;

    loadCytoscape().then((cy) => {
      if (!mounted || !containerRef.current) return;
      if (cyRef.current) return; // Already initialized

      const instance = cy({
        container: containerRef.current,
        elements: [],
        style: buildCytoscapeStyles('dark'),
        layout: { name: 'preset' },
        minZoom: 0.2,
        maxZoom: 4,
        wheelSensitivity: 0.3,
      });

      // Click node → select
      instance.on('tap', 'node', (evt: any) => {
        const nodeId = evt.target.id();
        onSelectNodeRef.current(nodeId);
      });

      // Double-click node → expand
      instance.on('dbltap', 'node', (evt: any) => {
        const nodeId = evt.target.id();
        onExpandNodeRef.current(nodeId);
      });

      // Click background → deselect
      instance.on('tap', (evt: any) => {
        if (evt.target === instance) {
          onSelectNodeRef.current(null);
        }
      });

      // Node hover → neighborhood focus + rich tooltip
      instance.on('mouseover', 'node', (evt: any) => {
        const node = evt.target;
        applyFocus(node.id());
        const data = node.data();
        const color = data.color || '#9fa4c0';
        const typeLabel = ENTITY_TYPE_LABELS[data.type] || data.type || '';
        const reports = data.report_count || 1;
        const pos = evt.renderedPosition || node.renderedPosition();
        showTooltip(
          `<strong>${escapeHtml(data.name || '')}</strong><br/>` +
            `<span style="color:${color}">●</span> ${escapeHtml(typeLabel)} · ${reports} report${reports === 1 ? '' : 's'}<br/>` +
            `<em style="opacity:0.7">double-click to expand</em>`,
          pos.x,
          pos.y,
        );
      });

      instance.on('mouseout', 'node', () => {
        restoreFocus();
        hideTooltip();
      });

      // Edge hover tooltip
      instance.on('mouseover', 'edge', (evt: any) => {
        const data = evt.target.data();
        const pos = evt.renderedPosition;
        showTooltip(
          `<strong>${escapeHtml(data.type || 'related')}</strong><br/>` +
            (data.edge_kind === 'INFERRED_REL'
              ? `confidence: ${(data.confidence || 0).toFixed(2)}`
              : `mentions: ${data.mention_count || '?'}`),
          pos.x,
          pos.y,
        );
      });

      instance.on('mouseout', 'edge', hideTooltip);

      // Tooltips are absolutely positioned: hide them while the viewport moves
      instance.on('pan', hideTooltip);
      instance.on('zoom', () => {
        hideTooltip();
        const all = instance.zoom() >= ZOOM_LABEL_THRESHOLD;
        if (all !== zoomAllLabelsRef.current) {
          zoomAllLabelsRef.current = all;
          refreshLabels();
        }
      });

      cyRef.current = instance;
      setCyReady(true);
    });

    return () => {
      mounted = false;
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
        setCyReady(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Load cluster data
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !cluster) return;

    // Clear existing graph and rebuild fresh (handles depth changes correctly)
    cy.elements().remove();

    cy.batch(() => {
      cluster.nodes.forEach((node: KGNode) => {
        const el = cy.add({
          group: 'nodes',
          data: {
            id: node.id,
            name: node.name,
            type: node.type,
            report_count: node.report_count || 1,
            color: ENTITY_TYPE_COLORS[node.type] || '#6b7280',
          },
        });
        if (hiddenTypesRef.current.includes(node.type)) el.addClass('hidden-type');
      });

      cluster.edges.forEach((edge: KGEdge) => {
        const edgeId = `${edge.source}-${edge.target}-${edge.type}`;
        if (cy.getElementById(edge.source).length && cy.getElementById(edge.target).length) {
          cy.add({
            group: 'edges',
            data: {
              id: edgeId,
              source: edge.source,
              target: edge.target,
              type: edge.type,
              edge_kind: edge.edge_kind,
              confidence: edge.confidence || 0,
              mention_count: edge.mention_count || 1,
            },
          });
        }
      });
    });

    // Randomize only when exploring a new root; keep positions stable otherwise
    // (depth / inferred toggles) so the graph grows instead of reshuffling.
    const rootChanged = rootNodeIdRef.current !== lastRootRef.current;
    lastRootRef.current = rootNodeIdRef.current;
    runLayout(rootChanged);

    // Restore selection state and interaction classes on the fresh elements
    const selId = selectedNodeIdRef.current;
    if (selId) {
      const sel = cy.getElementById(selId);
      if (sel.length) sel.select();
    }
    restoreFocus();
    refreshLabels();
  }, [cluster, runLayout, restoreFocus, refreshLabels]);

  // Selection highlight + focus
  useEffect(() => {
    selectedNodeIdRef.current = selectedNodeId;
    const cy = cyRef.current;
    if (!cy) return;
    cy.nodes().unselect();
    if (selectedNodeId) {
      const node = cy.getElementById(selectedNodeId);
      if (node.length) node.select();
    }
    restoreFocus();
    refreshLabels();
  }, [selectedNodeId, restoreFocus, refreshLabels]);

  // Legend type filter
  useEffect(() => {
    hiddenTypesRef.current = hiddenTypes;
    const cy = cyRef.current;
    if (!cy) return;
    cy.batch(() => {
      cy.nodes().forEach((n: any) => n.toggleClass('hidden-type', hiddenTypes.includes(n.data('type'))));
    });
    refreshLabels();
  }, [hiddenTypes, refreshLabels]);

  // Layout mode toggle (force ↔ radial)
  useEffect(() => {
    layoutModeRef.current = layoutMode;
    if (cyRef.current && cluster) runLayout(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutMode]);

  const handleZoomIn = useCallback(() => {
    cyRef.current?.zoom({ level: cyRef.current.zoom() * 1.3, renderedPosition: { x: containerRef.current!.clientWidth / 2, y: containerRef.current!.clientHeight / 2 } });
  }, []);

  const handleZoomOut = useCallback(() => {
    cyRef.current?.zoom({ level: cyRef.current.zoom() / 1.3, renderedPosition: { x: containerRef.current!.clientWidth / 2, y: containerRef.current!.clientHeight / 2 } });
  }, []);

  const handleFit = useCallback(() => {
    cyRef.current?.fit(undefined, 40);
  }, []);

  const handleExportPng = useCallback(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const uri = cy.png({ full: true, scale: 2, bg: '#1a1d2e' });
    const a = document.createElement('a');
    a.href = uri;
    a.download = `stix-constellation-${rootNodeIdRef.current || 'graph'}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, []);

  // Mobile fallback
  if (isMobile) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <HubIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.4, mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          Graph visualization is optimized for desktop. Use search and entity details on mobile.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100%', minHeight: 400 }}>
      {/* Controls overlay */}
      <GraphControls
        depth={depth}
        onDepthChange={onDepthChange}
        showInferred={showInferred}
        onShowInferredChange={onShowInferredChange}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onFit={handleFit}
        onExportPng={handleExportPng}
        layoutMode={layoutMode}
        onLayoutModeChange={setLayoutMode}
        legendItems={legendItems}
        hiddenTypes={hiddenTypes}
        onToggleType={onToggleType}
      />

      {/* Truncation chip (compact, out of the way) */}
      {truncated && (
        <Tooltip
          title={`Showing the top ${MAX_GRAPH_NODES} of ${totalNodeCount} connections, sorted by report count. Use depth 1 or hide entity types from the legend for full detail.`}
        >
          <Chip
            icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
            label={`${MAX_GRAPH_NODES}/${totalNodeCount} shown`}
            size="small"
            color="warning"
            variant="outlined"
            sx={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              zIndex: 10,
              bgcolor: (t) => alpha(t.palette.background.paper, 0.9),
              backdropFilter: 'blur(6px)',
            }}
          />
        </Tooltip>
      )}

      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => alpha(theme.palette.background.default, 0.6),
            zIndex: 5,
            borderRadius: 2,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Empty state */}
      {!cluster && !loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <HubIcon sx={{ fontSize: 120, color: 'text.secondary', opacity: 0.15, mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Select an entity above to explore its connections
          </Typography>
        </Box>
      )}

      {/* Cytoscape container */}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 400,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: 'radial-gradient(ellipse at 50% 35%, #222745 0%, #1a1d2e 75%)',
        }}
      />

      {/* Node/edge tooltip (positioned div, not MUI Tooltip) */}
      <Box
        ref={tooltipRef}
        sx={{
          display: 'none',
          position: 'absolute',
          pointerEvents: 'none',
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
          color: 'text.primary',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          px: 1,
          py: 0.5,
          fontSize: '0.75rem',
          lineHeight: 1.5,
          zIndex: 20,
          whiteSpace: 'nowrap',
          boxShadow: 3,
        }}
      />
    </Box>
  );
};

export default ConstellationGraph;
